import React, { useCallback, useState } from 'react';
import TableCell from './TableCell';
import { sanitizeForCopy } from './utils/formatters';
import { copyText } from '../../utils/clipboard';

function TableRow({
    row,
    rowIndex,
    columns,
    visibleColumns,
    rowKey,
    sliceStart,
    isSelected,
    selectable,
    onToggleSelect,
    onRowClick,
    onImageClick,
    onTextOverflow,
    conditionalFormats,
    stickyOffsets,
}) {
    const [hovered, setHovered] = useState(false);

    const getCellValue = useCallback(
        (col) => {
            if (Array.isArray(row)) {
                const realIdx = columns.indexOf(col);
                return row[realIdx];
            }
            return row[col.key];
        },
        [row, columns]
    );

    const handleRowCopy = useCallback(
        async (e) => {
            e.stopPropagation();
            const values = visibleColumns.map((col) => sanitizeForCopy(getCellValue(col)));
            await copyText(values.join('\t'));
        },
        [visibleColumns, getCellValue]
    );

    const getConditionalClass = useCallback(
        (col, value) => {
            if (!conditionalFormats || !conditionalFormats.length) return '';
            const match = conditionalFormats.find(
                (cf) => cf.column === col.key && cf.condition(value, row)
            );
            return match ? match.className : '';
        },
        [conditionalFormats, row]
    );

    const trClassName = [
        'ut-tr',
        hovered ? 'ut-tr--hover' : '',
        isSelected ? 'ut-tr--selected' : '',
    ].filter(Boolean).join(' ');

    return (
        <tr
            className={trClassName}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
        >
            <td className="ut-td ut-td--row-actions ut-td--sticky" style={{ left: 0 }}>
                <div className="ut-row-actions">
                    {hovered && (
                        <button
                            type="button"
                            className="ut-row-copy"
                            onClick={handleRowCopy}
                            title="Скопировать строку"
                            aria-label="Скопировать строку"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M4 2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6z" />
                                <path d="M2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1H8v1H2V6h1V5H2z" />
                            </svg>
                        </button>
                    )}
                    {selectable && (
                        <input
                            type="checkbox"
                            className="ut-row-checkbox"
                            checked={isSelected}
                            onChange={() => onToggleSelect(rowKey)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            </td>

            {visibleColumns.map((col) => {
                const value = getCellValue(col);
                const stickyLeft = stickyOffsets[col.key];

                return (
                    <TableCell
                        key={col.key}
                        value={value}
                        column={col}
                        row={row}
                        onImageClick={onImageClick}
                        onTextOverflow={onTextOverflow}
                        stickyLeft={stickyLeft}
                        conditionalClass={getConditionalClass(col, value)}
                    />
                );
            })}
        </tr>
    );
}

export default React.memo(TableRow);
